import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import CyberBrand from "../../components/common/CyberBrand";
import ThemeToggle from "../../components/ThemeToggle";
import { scrollToSection } from "./scrollToSection";

const NAV_GROUPS = [
  {
    label: "Platform",
    items: [
      { label: "[Capabilities]", href: "#features" },
      { label: "[Cyber Range]", href: "#cyber-range" },
      { label: "[Labs]", href: "#labs" },
      { label: "[Dashboards]", href: "#dashboards" },
      { label: "[Certification]", href: "#tksa" },
    ],
  },
  {
    label: "AI",
    items: [
      { label: "[AI Copilots]", href: "#ai" },
      { label: "[Topology Designer]", href: "#topology" },
    ],
  },
  { label: "Solutions", href: "#solutions" },
];

/* The shared "bracket syntax" label — [ platform ] — whose brackets light up
 * with the brand color when the enclosing `group` is hovered or focused. */
function BracketLabel({ children }) {
  const bracket =
    "text-(--color-text-muted) transition-colors group-hover:text-(--color-primary) group-focus-within:text-(--color-primary)";
  return (
    <span className="whitespace-nowrap lowercase tracking-tight">
      <span className={bracket}>[</span>
      <span className="px-1.5">{children}</span>
      <span className={bracket}>]</span>
    </span>
  );
}

/*
 * Runs a packet around the perimeter of whatever it wraps — the same motif as
 * the hero CTAs, the stats orbs and the engine spokes.
 *
 * The path is measured from the child's real size with a ResizeObserver, so it
 * hugs the button at any width (the labels are mono, so they shift as fonts
 * load) without hard-coded dimensions. The path itself is never stroked; only
 * the moving packet is visible.
 */
function OrbitTrack({
  children,
  delay = "0s",
  pad = 4,
  radius = 11,
  size = 2,
}) {
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width && height) setBox({ w: width, h: height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const w = box ? box.w + pad * 2 : 0;
  const h = box ? box.h + pad * 2 : 0;
  const r = radius;
  const ar = radius - 1;

  /* a rounded rect traced clockwise from the end of the top-left arc */
  const path = box
    ? `M${r},1 H${w - r} A${ar},${ar} 0 0 1 ${w - 1},${r} V${h - r} A${ar},${ar} 0 0 1 ${
        w - r
      },${h - 1} H${r} A${ar},${ar} 0 0 1 1,${h - r} V${r} A${ar},${ar} 0 0 1 ${r},1 Z`
    : "";

  return (
    <span ref={ref} className="relative inline-flex">
      {box && !reduce && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            left: -pad,
            top: -pad,
            width: w,
            height: h,
            overflow: "visible",
          }}
        >
          <circle
            r={size}
            fill="var(--color-primary)"
            style={{
              filter: "drop-shadow(0 0 4px var(--color-shadow-primary))",
            }}
          >
            <animateMotion
              dur="4s"
              repeatCount="indefinite"
              begin={delay}
              path={path}
            />
          </circle>
        </svg>
      )}
      {children}
    </span>
  );
}

/*
 * Public marketing navbar — a glass bar that grows a border + shadow once the
 * page scrolls. Separate from the app's internal Navbar (which carries the
 * user menu / search) so nothing auth-gated leaks onto the landing page.
 *
 * Nav links use a "bracket syntax" treatment: monospace, lowercase, each label
 * wrapped in dimmed [ ] brackets that light up to the brand color on hover.
 */
export default function LandingNav({ onLogin, onSignup }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavClick = (event, href) => {
    event.preventDefault();
    // Dropdown panels stay open while something inside them holds focus.
    event.currentTarget.blur();
    setMobileOpen(false);
    // An open drawer locks body scroll; let that unwind before we scroll.
    requestAnimationFrame(() => scrollToSection(href));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        dir="ltr"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "qv-glass-bar shadow-sm dark:shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
            : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-350 items-center justify-between gap-6 px-5 md:px-10 py-3">
          <Link
            to="/"
            className="flex items-center"
            aria-label="Quantum Vanguard home"
          >
            <CyberBrand size="md" showSubtitle={false} />
          </Link>

          {/* Desktop anchor links — bracket syntax, grouped into dropdowns.
              Hover and keyboard focus both open a panel; no JS state needed. */}
          <nav className="hidden lg:flex items-center gap-3 xl:gap-4 font-mono text-sm">
            {NAV_GROUPS.map((group) =>
              group.items ? (
                <div key={group.label} className="group relative">
                  <button
                    type="button"
                    aria-haspopup="true"
                    className="flex items-center gap-1 px-1 py-2 text-(--color-text-secondary) transition-colors hover:text-(--color-primary) group-focus-within:text-(--color-primary)"
                  >
                    <BracketLabel>{group.label}</BracketLabel>
                    <ChevronDown className="h-3.5 w-3.5 text-(--color-text-muted) transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180" />
                  </button>

                  {/* `pt-2` bridges the gap under the trigger so the panel
                      doesn't close while the cursor travels into it. */}
                  <div className="invisible absolute left-1/2 top-full z-10 w-56 -translate-x-1/2 translate-y-1 pt-2 opacity-0 transition-[opacity,transform] duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                    <div className="qv-glass-sidebar rounded-xl border border-(--color-border) p-1.5 shadow-lg dark:shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
                      {group.items.map((item) => (
                        <a
                          key={item.href}
                          href={item.href}
                          onClick={(e) => handleNavClick(e, item.href)}
                          className="block rounded-lg px-3 py-2 text-[13px] lowercase text-(--color-text-secondary) transition-colors hover:bg-(--color-bg-card) hover:text-(--color-primary)"
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={group.href}
                  href={group.href}
                  onClick={(e) => handleNavClick(e, group.href)}
                  className="group px-1 py-2 text-(--color-text-secondary) transition-colors hover:text-(--color-primary)"
                >
                  <BracketLabel>{group.label}</BracketLabel>
                </a>
              ),
            )}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            {/* the nav buttons are smaller than the hero's, so the packet and
                its orbit are scaled down to suit */}
            <OrbitTrack pad={4} radius={11} size={2}>
              {/* opens the auth console over the page — no route change */}
              <button
                type="button"
                onClick={onLogin}
                className="px-4 py-2 rounded-lg font-mono lowercase text-sm text-(--color-text-primary) border border-(--color-border-solid) hover:border-(--color-primary) hover:text-(--color-primary) transition-all duration-300"
              >
                login
              </button>
            </OrbitTrack>
            <OrbitTrack pad={4} radius={11} size={2} delay="2s">
              <button
                type="button"
                onClick={onSignup}
                className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-mono lowercase text-sm font-semibold text-(--color-text-on-primary) bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))] shadow-[0_0_14px_var(--color-shadow-primary)] hover:shadow-[0_0_26px_var(--color-shadow-primary-hover)] hover:-translate-y-px transition-all duration-300"
              >
                ./signup
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
            </OrbitTrack>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-card) transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div
        dir="ltr"
        className={`fixed top-0 right-0 z-50 h-full w-72 qv-glass-sidebar lg:hidden flex flex-col transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
          <CyberBrand size="sm" showSubtitle={false} />
          <button
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg) transition-colors"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* The drawer has the room to list every section, so groups become
            captioned blocks rather than dropdowns. */}
        <nav className="flex flex-col overflow-y-auto px-3 py-4 font-mono">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="pt-3 first:pt-0">
              {group.items && (
                <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-(--color-text-muted)">
                  {group.label}
                </p>
              )}
              {(group.items ?? [group]).map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="group block rounded-lg px-3 py-2.5 text-sm text-(--color-text-secondary) hover:bg-(--color-bg-card) hover:text-(--color-primary) transition-colors"
                >
                  <BracketLabel>{item.label}</BracketLabel>
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className="mt-auto px-4 py-5 border-t border-(--color-border) space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-(--color-text-muted)">Theme</span>
            <ThemeToggle />
          </div>
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              onLogin();
            }}
            className="block w-full text-center px-4 py-2.5 rounded-lg font-mono lowercase text-sm text-(--color-text-primary) border border-(--color-border-solid) hover:border-(--color-primary) transition-all"
          >
            login
          </button>
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false);
              onSignup();
            }}
            className="block w-full text-center px-4 py-2.5 rounded-lg font-mono lowercase text-sm font-semibold text-(--color-text-on-primary) bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))] shadow-[0_0_14px_var(--color-shadow-primary)] transition-all"
          >
            ./signup
          </button>
        </div>
      </div>
    </>
  );
}
